import React, { useMemo, useState } from 'react';
import { Box } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import ComponentSlider from 'src/mtska-frontend-app-component/components/Slider/ComponentSlider';
import { useDashboardFiltersStorageKey } from 'src/mtska-frontend-modules/modules/_shared/dashboardFilters/DashboardFiltersStorageKeyContext';
import { Button } from 'src/mtska-frontend-app-component';
import { useSettingContextInComponent, useSyncDashboardFullscreenClass } from 'src/mtska-frontend-app-scaffolding';
import {
    getRentalRunningReportTitle,
    resolveRentalRunningReport,
} from 'src/mtska-frontend-modules/modules/Dashboard/views/components/RentalDashboard/rentalRunning/reportResolver';
import { useDashboardReportConfiguration } from 'src/mtska-frontend-modules/modules/_shared/dashboardFilters/useDashboardReportConfiguration';
import { getSectionIdsForDashboardModule } from 'src/mtska-frontend-modules/modules/_shared/dashboardFilters/dashboardModuleSectionMap';
import {
    DashboardReportPrintModal,
    flattenReportPages,
    useDashboardReportPrint,
} from 'src/mtska-frontend-modules/modules/_shared/dashboardConfigured';

const Container = ({ dataProviderName, filters: defaultFilters, reportTitleDefault, reportVisibleDefault = false }) => {
    const { t } = useTranslation();
    useSyncDashboardFullscreenClass();
    const [reportVisible, setReportVisible] = useState(reportVisibleDefault);
    const { filterModel } = useSettingContextInComponent(useDashboardFiltersStorageKey(dataProviderName), defaultFilters);
    const { reportConfiguration } = useDashboardReportConfiguration();
    const mergedFilters = filterModel ?? defaultFilters;
    const filtersKey = useMemo(() => JSON.stringify(mergedFilters ?? null), [mergedFilters]);
    const sectionIds = useMemo(() => getSectionIdsForDashboardModule('fleetcustomer'), []);
    const report = useMemo(
        () =>
            resolveRentalRunningReport({
                t,
                filters: mergedFilters,
                keyPrefix: 'fleetcustomer',
                filtersStorageKey: dataProviderName,
                sectionIds,
                reportConfiguration,
            }),
        [t, mergedFilters, filtersKey, dataProviderName, sectionIds, reportConfiguration],
    );
    const reportTitle = getRentalRunningReportTitle(reportConfiguration);
    const printPages = useMemo(() => flattenReportPages(report), [report]);
    const hasPrintablePages = printPages.length > 0;
    const { printModalOpen, openPrintModal, closePrintModal } = useDashboardReportPrint();
    const printedAtLabel = useMemo(() => new Date().toLocaleString(), []);

    return (
        <Box className="mtska-dashboard-container" sx={{ position: 'relative' }}>
            <Box className="dashboard-section-title" sx={{ marginTop: '30px', marginBottom: '10px' }}>
                <h2>{t(reportTitleDefault ?? reportTitle)}</h2>
            </Box>

            <Box className="dashboard-report-visibility-wrapper">
                <Button
                    className="dashboard-report-visibility-toggler"
                    onClick={() => setReportVisible(!reportVisible)}
                    variant="outlined"
                >
                    {reportVisible ? t('Hide Business Review') : t('Show Business Review')}
                </Button>
            </Box>

            {reportVisible &&
                <>
                    <ComponentSlider report={report} />
                    <DashboardReportPrintModal
                        open={printModalOpen}
                        onClose={closePrintModal}
                        report={report}
                        title={t(reportTitle)}
                        printedAtLabel={printedAtLabel}
                        documentTitle={t(reportTitle)}
                        filterModel={mergedFilters}
                    />

                    <Box className="mtska-dashboard-toolbar">
                        <Button
                            variant="soft"
                            size="sm"
                            icon="faPrint"
                            onClick={openPrintModal}
                            disabled={!hasPrintablePages}
                            aria-label={t('print')}
                            title={t('print')}
                        />
                        <Button
                            variant="soft"
                            size="sm"
                            icon="faExpand"
                            onClick={() => {
                                const parent = document.querySelector('.mtska-dashboard-container');
                                if (!parent) return;
                                if (document.fullscreenElement || document.webkitFullscreenElement) {
                                    parent.classList.remove('mtska-fullscreen');
                                    if (document.exitFullscreen) {
                                        document.exitFullscreen();
                                    } else if (document.webkitExitFullscreen) {
                                        document.webkitExitFullscreen();
                                    }
                                } else if (parent.requestFullscreen) {
                                    parent
                                        .requestFullscreen()
                                        .then(() => parent.classList.add('mtska-fullscreen'))
                                        .catch(() => parent.classList.remove('mtska-fullscreen'));
                                } else if (parent.webkitRequestFullscreen) {
                                    parent.webkitRequestFullscreen();
                                    parent.classList.add('mtska-fullscreen');
                                }
                            }}
                            aria-label={t('Fullscreen')}
                        />
                    </Box>
                </>
            }


        </Box>
    );
};

export default Container;
